import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, MotionConfig } from 'framer-motion';
import { SectionContainer, AnimatedHeading, GlassCard, StatBadge, TechTag } from '../components/ui';
import {
  ScrollProgress, Reveal, WordReveal, TiltCard, HoverIcon, Parallax,
} from '../components/motion/ScrollMotion';

/* ── Pipeline Visualization ─────────────────────────────────────────────────── */
const PIPELINE_STAGES = [
  { id: 'binary',   label: 'Binary Input', icon: '⬡', desc: 'PE / ELF executables',     color: '#6b8fa8' },
  { id: 'extract',  label: 'Extraction',   icon: '◈', desc: 'Radare2 (Ghidra if present)',color: '#00f0ff' },
  { id: 'behav',    label: 'Behaviour',    icon: '◎', desc: 'Static import behaviour',    color: '#00c8ff' },
  { id: 'cap',      label: 'Capability',   icon: '◆', desc: 'CAPA rules (if enabled)',    color: '#009fcc' },
  { id: 'mitre',    label: 'MITRE',        icon: '◉', desc: 'ATT&CK mapping',             color: '#ff8c42' },
  { id: 'risk',     label: 'Risk Score',   icon: '▲', desc: 'Heuristic scoring',          color: '#ffb347' },
  { id: 'cvss',     label: 'CVSS',         icon: '◐', desc: 'Heuristic severity',         color: '#ff6b6b' },
  { id: 'verdict',  label: 'Verdict',      icon: '★', desc: 'Final classification',        color: '#ff3c6e' },
];

function PipelineViz() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive(a => (a + 1) % PIPELINE_STAGES.length), 1400);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ position: 'relative', padding: '1.5rem 0' }}>
      {/* Connection line — hidden when items wrap */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: '42%', left: '3%', right: '3%', height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(0,240,255,0.18), transparent)',
        pointerEvents: 'none',
      }} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0.75rem 0.5rem',
      }}>
        {PIPELINE_STAGES.map((stage, i) => (
          <motion.div
            key={stage.id}
            animate={{
              scale:   active === i ? 1.06 : 1,
              opacity: active === i ? 1 : active > i ? 0.75 : 0.38,
            }}
            transition={{ duration: 0.3 }}
            style={{ textAlign: 'center' }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 8, margin: '0 auto 0.4rem',
              border: `1px solid ${active === i ? stage.color : 'rgba(0,240,255,0.12)'}`,
              background: active === i ? `${stage.color}15` : 'rgba(6,18,36,0.6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, color: active === i ? stage.color : '#6b8fa8',
              boxShadow: active === i ? `0 0 18px ${stage.color}35` : 'none',
              transition: 'all 0.3s',
            }}>
              {stage.icon}
            </div>
            <div style={{
              fontFamily: 'Space Mono', fontSize: 8,
              color: active === i ? stage.color : '#6b8fa8',
              letterSpacing: 0.8, textTransform: 'uppercase', lineHeight: 1.3,
            }}>
              {stage.label}
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        key={active}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          textAlign: 'center', marginTop: '1.25rem',
          fontFamily: 'Space Mono', fontSize: 10,
          color: PIPELINE_STAGES[active].color, letterSpacing: 1,
        }}
      >
        ▸ {PIPELINE_STAGES[active].desc}
      </motion.div>
    </div>
  );
}

/* ── Feature Card ───────────────────────────────────────────────────────────── */
function FeatureCard({ icon, title, desc, color = '#00f0ff', delay = 0 }) {
  return (
    <Reveal delay={delay} y={36} blur={5} style={{ height: '100%' }}>
      <TiltCard color={color} fill>
        <GlassCard hover glow color={color} style={{ height: '100%' }}>
          <HoverIcon
            color={color}
            restShadow={`0 0 14px ${color}18`}
            style={{
              width: 48, height: 48, borderRadius: 10, flexShrink: 0,
              background: `${color}12`, border: `1px solid ${color}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, marginBottom: '1rem',
            }}
          >
            <span aria-hidden="true">{icon}</span>
          </HoverIcon>
          <h3 style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 17, color: '#e0f4ff', letterSpacing: 0.4, marginBottom: '0.5rem' }}>
            {title}
          </h3>
          <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#6b8fa8', lineHeight: 1.7 }}>
            {desc}
          </p>
        </GlassCard>
      </TiltCard>
    </Reveal>
  );
}

/* ── Landing Page ───────────────────────────────────────────────────────────── */
function LandingContent() {
  return (
    <div className="page-wrapper">
      <ScrollProgress />

      {/* ── Hero ── */}
      <section
        aria-label="Hero"
        style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center',
          padding: 'clamp(5rem,10vh,7rem) clamp(1rem,4vw,2rem) 4rem',
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Background radial glow */}
        <div aria-hidden="true" style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 'min(900px,100vw)', height: 'min(900px,100vh)',
          background: 'radial-gradient(ellipse, rgba(0,240,255,0.045) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', position: 'relative', zIndex: 1 }}>
          {/* Two-column hero: stacks on mobile */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
            gap: 'clamp(2rem,5vw,4rem)',
            alignItems: 'center',
          }}
            className="arise-hero-grid"
          >
            {/* Left: copy */}
            <div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{
                  fontFamily: 'Space Mono', fontSize: 10, color: '#00f0ff',
                  letterSpacing: 3, textTransform: 'uppercase',
                  marginBottom: '1.5rem',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <span aria-hidden="true" style={{ width: 28, height: 1, background: '#00f0ff', display: 'inline-block', flexShrink: 0 }} />
                Advanced Threat Intelligence
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 36 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, delay: 0.1 }}
                style={{
                  fontFamily: 'Orbitron, monospace',
                  fontSize: 'clamp(2rem, 4.5vw, 3.75rem)',
                  fontWeight: 900, lineHeight: 1.08,
                  letterSpacing: '0.04em', marginBottom: '1.5rem',
                }}
              >
                <span style={{ color: '#e0f4ff' }}>NEXT-GEN</span><br />
                <span style={{
                  color: '#00f0ff',
                  textShadow: '0 0 36px rgba(0,240,255,0.5), 0 0 72px rgba(0,240,255,0.2)',
                  animation: 'flicker 8s infinite',
                }}>MALWARE</span><br />
                <span style={{ color: '#e0f4ff' }}>ANALYSIS</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.3 }}
                style={{ fontFamily: 'Inter', fontSize: 15, color: '#6b8fa8', lineHeight: 1.8, marginBottom: '2.25rem', maxWidth: 440 }}
              >
                Upload a Windows PE or Linux ELF executable and get an automated static-analysis report with candidate MITRE ATT&CK mappings. Uses Radare2, plus CAPA and Ghidra when they are available on the server; every report states which engines ran.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.5 }}
                style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}
              >
                <Link to="/analyze" className="btn-primary" style={{ fontSize: 12, padding: '11px 28px' }}>
                  ▸ Analyze File
                </Link>
                <Link to="/how-it-works" className="btn-ghost" style={{ fontSize: 11, padding: '11px 20px' }}>
                  How It Works
                </Link>
              </motion.div>

              {/* Stat row */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                style={{ display: 'flex', gap: 'clamp(1rem,3vw,2rem)', marginTop: '2.5rem', flexWrap: 'wrap' }}
              >
                {[
                  { value: '7-Stage', label: 'Pipeline'  },
                  { value: 'Static', label: 'Analysis'  },
                  { value: 'Engine', label: 'Disclosure' },
                ].map(s => (
                  <div key={s.label}>
                    <div style={{ fontFamily: 'Orbitron', fontWeight: 700, fontSize: 'clamp(14px,2vw,18px)', color: '#00f0ff' }}>{s.value}</div>
                    <div style={{ fontFamily: 'Space Mono', fontSize: 9, color: '#6b8fa8', letterSpacing: 1, textTransform: 'uppercase' }}>{s.label}</div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right: pipeline viz */}
            <Parallax distance={14}>
            <motion.div
              initial={{ opacity: 0, x: 36 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.85, delay: 0.4 }}
            >
              <GlassCard glow style={{ padding: 'clamp(1.25rem,3vw,2rem)' }}>
                <div style={{
                  fontFamily: 'Space Mono', fontSize: 10, color: '#6b8fa8',
                  letterSpacing: 2, textTransform: 'uppercase',
                  marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <div className="pulse" style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: '#00ff88', flexShrink: 0,
                  }} />
                  ARISE Pipeline — Live
                </div>
                <PipelineViz />
              </GlassCard>
            </motion.div>
            </Parallax>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <SectionContainer style={{ paddingTop: '1.5rem', paddingBottom: '2.5rem' }}>
        <div className="grid-4" style={{ gap: '1rem' }}>
          {[
            { value: '7',        label: 'Analysis Stages',  color: '#00f0ff' },
            { value: 'PE / ELF', label: 'Primary Formats',  color: '#00c8ff' },
            { value: '0–10',     label: 'Heuristic Severity', color: '#ffb347' },
            { value: 'Per-report', label: 'Engine Status', color: '#00ff88' },
          ].map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} y={20} blur={0} style={{ height: '100%' }}>
              <StatBadge value={s.value} label={s.label} color={s.color} />
            </Reveal>
          ))}
        </div>
      </SectionContainer>

      {/* ── Features ── */}
      <SectionContainer as="section" aria-labelledby="features-heading">
        <AnimatedHeading
          center
          subtitle="Automated static analysis. Results are triage aids, not a substitute for expert review; each report lists engines that were unavailable."
          level={2}
        >
          <span id="features-heading"><WordReveal inView text="Platform " style={{ color: '#e0f4ff' }} /></span>
          <WordReveal inView text="Capabilities" className="neon-text" delay={0.3} />
        </AnimatedHeading>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
          gap: '1.25rem',
          alignItems: 'stretch',
        }}
          className="arise-features-grid"
        >
          <FeatureCard icon="⬡" title="Static Extraction"          desc="Radare2 extracts functions, imports and strings. Ghidra is used only when installed on the server; reports say when it is not available." color="#00f0ff" delay={0} />
          <FeatureCard icon="◆" title="CAPA Capability Mapping"    desc="CAPA rules identify capabilities such as process injection or persistence. CAPA can be disabled on low-memory hosts; reports then mark it Disabled. If CAPA is disabled, skipped or fails, a BENIGN verdict is then capped at 50% confidence (MALICIOUS and SUSPICIOUS verdicts are not capped)." color="#00c8ff" delay={0.1} />
          <FeatureCard icon="◉" title="MITRE ATT&CK Mapping"       desc="Detected capabilities are mapped to candidate MITRE ATT&CK technique IDs for analyst review." color="#ff8c42" delay={0.2} />
          <FeatureCard icon="▲" title="Heuristic Risk Scoring"     desc="Rule-based heuristics produce a 0–100 risk score from imports, strings and detected capabilities." color="#ffb347" delay={0} />
          <FeatureCard icon="◐" title="Heuristic Severity"         desc="A CVSS-style 0–10 severity estimate. It is not an official CVSS v3.1 vector and is not derived from NVD." color="#ff6b6b" delay={0.1} />
          <FeatureCard icon="★" title="Verdict Engine"             desc="Final classification with confidence percentage — MALICIOUS, SUSPICIOUS, or BENIGN — with threat category." color="#ff3c6e" delay={0.2} />
        </div>
      </SectionContainer>

      {/* ── Tech Stack ── */}
      <SectionContainer
        as="section"
        aria-labelledby="tech-heading"
        style={{ background: 'rgba(0,240,255,0.015)', borderTop: '1px solid rgba(0,240,255,0.06)', borderBottom: '1px solid rgba(0,240,255,0.06)' }}
      >
        <AnimatedHeading center subtitle="Built on open-source security tools." level={2}>
          <span id="tech-heading"><WordReveal inView text="Powered By " style={{ color: '#e0f4ff' }} /></span>
          <WordReveal inView text="Elite Tooling" className="neon-text" delay={0.3} />
        </AnimatedHeading>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          {['Radare2', 'CAPA (optional)', 'Ghidra (optional)', 'MITRE ATT&CK', 'Python FastAPI', 'PostgreSQL', 'React'].map((t, i) => (
            <Reveal key={t} delay={i * 0.05} y={12} blur={0}>
              <TechTag>{t}</TechTag>
            </Reveal>
          ))}
        </div>
      </SectionContainer>

      {/* ── CTA ── */}
      <SectionContainer as="section" aria-labelledby="cta-heading">
        <Reveal y={32} scale={0.97}>
          <GlassCard glow style={{ textAlign: 'center', padding: 'clamp(2.5rem,6vw,4rem) clamp(1.5rem,5vw,3rem)' }}>
            <div style={{ fontFamily: 'Space Mono', fontSize: 10, color: '#6b8fa8', letterSpacing: 3, textTransform: 'uppercase', marginBottom: '1rem' }}>
              Ready to analyze?
            </div>
            <h2
              id="cta-heading"
              style={{ fontFamily: 'Orbitron', fontSize: 'clamp(1.75rem,4vw,2.75rem)', fontWeight: 900, color: '#e0f4ff', marginBottom: '1rem', letterSpacing: '0.04em', lineHeight: 1.15 }}
            >
              <WordReveal inView text="DROP A BINARY." /><br />
              <WordReveal inView delay={0.45} text="GET THE TRUTH." style={{ color: '#00f0ff', textShadow: '0 0 28px rgba(0,240,255,0.5)' }} />
            </h2>
            <p style={{ fontFamily: 'Inter', fontSize: 15, color: '#6b8fa8', maxWidth: 480, margin: '0 auto 2.5rem', lineHeight: 1.7 }}>
              Upload a PE or ELF sample and receive an automated static-analysis report that states which engines ran.
            </p>
            <Link to="/analyze" className="btn-primary" style={{ fontSize: 13, padding: '13px 44px' }}>
              ▸ Start Analysis
            </Link>
          </GlassCard>
        </Reveal>
      </SectionContainer>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 860px) {
          .arise-hero-grid     { grid-template-columns: 1fr !important; }
          .arise-features-grid { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
        }
        @media (max-width: 540px) {
          .arise-features-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

/* reducedMotion="user" makes every framer animation on this page skip transform
 * movement when the OS asks for reduced motion (the original page ignored it). */
export default function LandingPage() {
  return (
    <MotionConfig reducedMotion="user">
      <LandingContent />
    </MotionConfig>
  );
}
